import { CommonModule } from '@angular/common';
import { ApplicationRef, Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LoginModalComponent } from '@kimbocare/auth';
import {
  CustomNavigationService,
  extractQueryParameters,
  InvitationResponse,
  IUser,
  LoadingService,
  ModalService,
  NotificationService,
  SessionService,
  UseCaseGroupService,
} from '@kimbocare/core';
import { GlobalSpinnerComponent, KUIModule } from '@kimbocare/ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { combineLatest, filter, first } from 'rxjs';
import { SubscriptionService } from './core/rest/usecases/subscription.service';
import { getSubdomain, redirectToTenant } from './core/utils/domain.utils';
import { TenantApiService } from './tenant/services/tenant-api.service';
import { TenantThemeService } from './tenant/services/tenant-theme.service';
import {
  exportAuthStateForTransfer,
  TENANT_COUNTRIES_KEY,
} from './core/utils/storage-transfer.utils';

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterOutlet,
    KUIModule,
    TranslateModule,
    MatButtonModule,
    GlobalSpinnerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  isGuest = false;
  isLoggedIn = false;
  user: IUser | null = null;

  constructor(
    private sessionService: SessionService,
    private appRef: ApplicationRef,
    private router: Router,
    private navigationService: CustomNavigationService,
    private useCaseGroup: UseCaseGroupService,
    private loadingService: LoadingService,
    private modalService: ModalService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private subscriptionService: SubscriptionService,
    private tenantService: TenantApiService,
    private themeService: TenantThemeService,
  ) {
    translateService.addLangs(['en', 'fr']);
    translateService.setDefaultLang('en');
    const previousChoseLanguage = this.sessionService.getLanguage();
    const browserLang = previousChoseLanguage
      ? previousChoseLanguage
      : translateService.getBrowserLang();
    if (browserLang)
      translateService.use(browserLang.match(/en|fr/) ? browserLang : 'en');

    this.sessionService
      .isGuest()
      .subscribe((isGuest) => (this.isGuest = isGuest));

    this.sessionService.isLoggedIn().subscribe((isLoggedIn) => {
      this.isLoggedIn = isLoggedIn;

      if (isLoggedIn) {
        this.user = this.sessionService.getAuthState().user;
        const currentGroup = this.sessionService.getCurrentGroup();
        // Tenant Routing Logic
        if (this.user) {
          // If the user has a TENANT role or type, fetch their tenant info
          // NOTE: Adjust this condition based on how 'Tenant' role is defined in your DB
          console.log('User group:', currentGroup);
          if (currentGroup == undefined) {
            this.tenantService.getTenantByUser(this.user.uuid).subscribe({
              next: (tenant) => {
                if (tenant && tenant.slug) {
                  localStorage.setItem('tenant_uuid', tenant.id);
                  // Persist the tenant's countries so the country listing can
                  // be scoped to this tenant. Saved before the auth-state
                  // export below so it is transferred across subdomains too.
                  if (tenant.countries) {
                    localStorage.setItem(
                      TENANT_COUNTRIES_KEY,
                      JSON.stringify(tenant.countries),
                    );
                  }
                  // Persist tenant brand to localStorage so it survives logout
                  // and applies pre-login on subsequent visits.
                  this.themeService.saveTenantConfig(tenant.config);
                  const currentSubdomain = getSubdomain();
                  // If we are not on the tenant's subdomain, redirect them with
                  // the auth state. If we already are, the user is exactly where
                  // they should be — do nothing and let them stay on the current
                  // route (this is the normal case on a page refresh).
                  if (currentSubdomain !== tenant.slug) {
                    const encodedState = exportAuthStateForTransfer();
                    redirectToTenant(tenant.slug, encodedState);
                  }
                } else {
                  // The user is not attached to any tenant — genuinely forbidden.
                  this.router.navigate(['/error403']);
                }
              },
              error: (err) => {
                console.error('Failed to load tenant info for user', err);
              },
            });
          } else {
            // Routine Benefit user checks (they stay on root domain)
            this.getSubs();
          }
        }
      }
    });

    this.user = this.sessionService.getAuthState().user;
    const currentGroup = this.sessionService.getCurrentGroup();
    // getSubs() is handled inside the subscription now, but we keep this for initialization if already logged in as Benefit
    if (this.user && currentGroup !== undefined) {
      this.getSubs();
    }
  }

  ngOnInit(): void {
    //Because query external parameters from angular app when app is starting  if too busy 😅
    const queryParams = extractQueryParameters(window.location.href, [
      'mode',
      'key',
      'identification',
      'invitation_key',
      'state',
    ]);
    combineLatest([
      this.appRef.isStable.pipe(first((isStable) => isStable)),
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        first(),
      ),
    ]).subscribe(() => {
      if (queryParams) {
        if (!this.isLoggedIn || this.isGuest) {
          if (
            queryParams['mode'] ||
            queryParams['key'] ||
            queryParams['invitation_key']
          ) {
            this.navigationService.goToWithParams('login', queryParams);
          } else if (queryParams['state']) {
            this.navigationService.goToWithParams('loginsuccess', queryParams);
          }
        } else if (this.isLoggedIn && !this.isGuest && !queryParams['state']) {
          if (queryParams['invitation_key']) {
            this.addPendindInvitation(queryParams['invitation_key']);
          } else {
            this.inviteMember(queryParams);
          }
        }
      }
    });
  }

  inviteMember(queryParams: any): void {
    this.loadingService.show();
    this.useCaseGroup.verifyAndAddMember(queryParams['key']).subscribe({
      next: (res: InvitationResponse) => {
        setTimeout(() => {
          if (res.status === 400) {
            this.notificationService.warning(
              this.translateService.instant(
                'common.wrong-invitation-account-message',
                {
                  identifier: queryParams['identification'],
                },
              ),
            );
            this.modalService
              .openModal(LoginModalComponent, {
                width: '950px',
                height: 'auto',
                data: {
                  caller: '_SIGN_IN',
                  invitation: {
                    key: queryParams['key'],
                    mode: queryParams['mode'],
                    identifier: queryParams['identification'],
                  },
                },
              })
              .subscribe();
          } else if (res.status === 200) {
            this.sessionService.storeCurrentGroup(res.group);
            this.notificationService.success(
              this.translateService.instant('common.invitation-success'),
            );
          } else if (res.status === 404) {
            this.notificationService.warning(
              this.translateService.instant(
                'common.invitation-not-exist-label',
              ),
            );
          }
        }, 1000);
      },
      error: () => {
        this.notificationService.danger(
          this.translateService.instant('errors.generic'),
        );
        this.loadingService.hide();
      },
      complete: () => {
        this.loadingService.hide();
      },
    });
  }

  addPendindInvitation(uuid: string): void {
    this.loadingService.show();
    this.useCaseGroup.addMember(uuid).subscribe({
      next: () => {
        this.notificationService.success(
          this.translateService.instant('common.add-pending-member'),
        );
      },
      error: (err) => {
        this.notificationService.danger(
          this.translateService.instant(err.translate ?? 'errors.generic'),
        );
        this.loadingService.hide();
      },
      complete: () => {
        this.loadingService.hide();
      },
    });
  }

  getSubs(): void {
    // Leave that like this for now
  }
}
